/**
 * Messages for the public pages (home, sign in, sign up, demo chooser, onboarding), English and
 * Arabic side by side (ADR-0026). Every key lives under `publicPages`.
 */
export const en = {
  publicPages: {
    header: {
      homeAria: '{{product}} home',
      siteNav: 'Site',
      products: 'Products',
      howItWorks: 'How it works',
      forAccountants: 'For accountants',
      indiaReady: 'India-ready',
      logIn: 'Log in',
      signUp: 'Sign up',
    },
    footer: {
      tagline: 'Accounting for growing businesses and the accountants who look after them.',
      products: 'Products',
      more: 'More',
      whoFor: 'Who it is for',
      businessOwners: 'Business owners',
      accountants: 'Accountants',
      practices: 'Practices',
      credits: '© {{year}} {{product}}. Photos from Unsplash by {{names}}.',
    },
    hero: {
      badge: 'Now with the AI Accountant',
      titleLead: 'The books your business runs on,',
      titleAccent: 'in one place',
      lead: '{{product}} brings invoicing, expenses, banking, GST and reports together, so business owners and their accountants work from the same numbers, every day.',
      getStarted: 'Get started',
    },
    trades: {
      title: 'Made for growing businesses',
      body: 'Cafés, restaurants, stores and studios with a team, suppliers and an accountant to keep happy.',
      cafes: 'Cafés',
      cafesAlt: 'A café owner seated in his café',
      restaurants: 'Restaurants',
      restaurantsAlt: 'A restaurant owner standing in his dining room',
      retail: 'Retail stores',
      retailAlt: 'A store owner with arms crossed among his shelves',
      studios: 'Studios and services',
      studiosAlt: 'A business owner at her office desk with a laptop',
    },
    productList: {
      title: 'Everything your books need, in one ledger',
      body: 'Start with invoicing and banking. Turn on inventory, projects and e-invoicing when you need them; it is all the same ledger underneath.',
      salesTitle: 'Invoicing and sales',
      salesBody:
        'Quotes, invoices, credit notes and payments, with recurring invoices and branded PDFs.',
      expensesTitle: 'Expenses and bills',
      expensesBody: 'Record expenses, pay vendor bills and raise purchase orders before you buy.',
      bankingTitle: 'Banking',
      bankingBody:
        'Import statements, categorise with rules and reconcile every account to the rupee.',
      gstTitle: 'GST and e-invoicing',
      gstBody:
        'GST worked out on every line, monthly summaries ready for filing, e-invoices on demand.',
      reportsTitle: 'Reports',
      reportsBody:
        'Profit and loss, balance sheet, trial balance and ageing, by class, location or project.',
      inventoryTitle: 'Inventory',
      inventoryBody: 'Stock on hand with FIFO costing, so cost of goods sold is right every month.',
      aiTitle: 'AI Accountant',
      aiBody: 'Drop in a bank statement or a bill; it reads each line and suggests how to book it.',
      migrationTitle: 'QuickBooks migration',
      migrationBody:
        'Bring lists, balances and history across, then compare month by month until they match.',
    },
    flows: {
      title: 'From the first invoice to the month-end close',
      sellLabel: 'Get paid',
      sellTitle: 'Send an invoice in a minute and see when it is paid',
      sellBody:
        'Create quotes and invoices with GST worked out for you, email them from Outss Accounting and record payments as they arrive.',
      sellP1: 'Quotes that turn into invoices in one click',
      sellP2: 'Recurring invoices for retainers and rent',
      sellP3: 'Ageing shows who owes what, and for how long',
      spendLabel: 'Control spend',
      spendTitle: 'Every rupee out, approved and accounted for',
      spendBody:
        'Raise purchase orders, turn them into bills and pay vendors on time. Expenses land in the right account with the right tax.',
      spendP1: 'Purchase orders that become bills',
      spendP2: 'Vendor balances and bills due at a glance',
      spendP3: 'Receipts and bills read for you by the AI Accountant',
      bankLabel: 'Reconcile',
      bankTitle: 'Bank lines that match themselves',
      bankBody:
        'Import a statement, let rules and matches do the routine work, and reconcile to the closing balance with confidence.',
      bankP1: 'Rules that categorise repeat transactions',
      bankP2: 'Matches against open invoices and bills',
      bankP3: 'A reconcile screen that shows the difference until it is zero',
      closeLabel: 'Close the month',
      closeTitle: 'Books your accountant can sign off',
      closeBody:
        'Lock periods once they are reviewed, keep a full audit log and run the reports that matter, for one business or many.',
      closeP1: 'Period close with a full audit trail',
      closeP2: 'Reports by class, location and project',
      closeP3: 'One place for the accountant and the business owner',
    },
    visuals: {
      invoiceNo: 'Invoice {{no}}',
      paid: 'Paid',
      catering: 'Catering supplies × 40',
      delivery: 'Delivery',
      total: 'Total',
      purchaseOrder: 'Purchase order',
      approved: 'Approved',
      bill: 'Bill',
      fromPo: 'From {{no}}',
      dueIn14: 'Due in 14 days',
      scheduled: 'Scheduled',
      bankHeader: '{{account}} · Bank transactions',
      matched: '{{n}} of {{total}} matched',
      ruleInternet: 'Rule: Internet',
      billFromPo: 'Bill from {{no}}',
      needsCategory: 'Needs a category',
      monthEndClose: 'Month-end close',
      progress: '{{n}} of {{total}}',
      reconciled: 'Bank accounts reconciled',
      gstReviewed: 'GST summary reviewed',
      signOff: 'Accountant sign-off',
      monthLocked: 'Month locked',
      done: 'Done',
      locked: 'Locked',
    },
    audiences: {
      eyebrow: 'Owners and accountants',
      title: 'One ledger, two points of view',
      ownersAlt: 'A café owner smiling in his café',
      ownersTitle: 'For business owners',
      ownersBody:
        'See cash, sales and what is owed on one screen. Send invoices, approve bills and answer your accountant without email chains.',
      ownersP1: 'The numbers that matter, on one dashboard',
      ownersP2: 'Approvals and a document inbox',
      ownersP3: 'Works the way QuickBooks users expect',
      accountantsAlt: 'An accountant working through client books on a laptop',
      accountantsTitle: 'For accountants and practices',
      accountantsBody:
        'Run every client from one login. Switch clients in a click, close periods, and hand over reports your clients can read.',
      accountantsP1: 'Client list with one-click switching',
      accountantsP2: 'Accountant tools, audit log and period close',
      accountantsP3: 'Migration from QuickBooks with a parallel run',
    },
    india: {
      photoAlt: 'A bright café with wooden tables by tall windows',
      title: 'Made for how Indian businesses keep books',
      body: 'GST, rupees and e-invoicing are set up from the start. Working with a client abroad? Other countries and currencies are a setting away.',
      gst: 'CGST, SGST and IGST worked out from the place of supply',
      rupee: 'Amounts in lakhs and crores, as your team reads them',
      hsn: 'HSN and SAC codes on products and invoice lines',
      eInvoiceKey: 'e-Invoice',
      eInvoice: 'E-invoicing when your turnover calls for it',
    },
    cta: {
      title: 'Bring your books to {{product}}',
      body: "Create your account, invite your accountant and start with today's numbers.",
    },
    preview: {
      windowTitle: '{{product}} · Dashboard',
      newButton: '+ New',
      dashboard: 'Dashboard',
      banking: 'Banking',
      sales: 'Sales',
      expenses: 'Expenses',
      reports: 'Reports',
      accounting: 'Accounting',
      overview: 'Business overview · {{company}}',
      thisQuarter: 'This quarter',
      income: 'Income',
      netProfit: 'Net profit',
      salesTrend: 'Sales trend',
      invoices: 'Invoices',
      overdue: 'Overdue',
      notDueYet: 'Not due yet',
      paid30: 'Paid (30 days)',
    },
    world: {
      eyebrow: 'Worldwide',
      titleLead: 'Invoice in their currency.',
      titleRest: 'Report in yours.',
      body: 'Invoices and bills in {{n}} currencies, with exchange rates by date, revaluation and the right decimal places for each.',
      currencies: 'currencies',
      reelTitle: 'One invoice total',
      play: 'Play the currency animation',
      pause: 'Pause the currency animation',
      example: 'For example, {{base}} is {{others}}.',
      decimals: 'KWD to three decimals, JPY to none.',
    },
    authCard: {
      homeAria: 'Home',
      backHome: 'Back to home',
    },
    login: {
      title: 'Log in to continue',
      newTo: 'New to Outss Accounting?',
      createAccount: 'Create an account',
      alreadySignedIn: 'You are already signed in.',
      open: 'Open Outss Accounting',
      failed: 'Could not open the sign-in page. Check your connection and try again.',
      email: 'Email',
      emailPlaceholder: 'Enter your email',
      opening: 'Opening sign-in…',
      continue: 'Continue',
      passwordNext: 'You will enter your password on the secure sign-in page next.',
    },
    signup: {
      title: 'Sign up to continue',
      subtitle:
        'Create your Outss Accounting account with your work email, then set up your company.',
      haveAccount: 'Already have an account?',
      failed: 'Could not open the sign-up page. Check your connection and try again.',
      workEmail: 'Work email',
      perk1: 'Invoicing, expenses, banking and GST in one place',
      perk2: 'Share the same books with your accountant',
      perk3: 'Reports by class, location and project',
      terms: 'By signing up, you agree to the terms of use and privacy policy of Outss.',
      opening: 'Opening sign-up…',
    },
    onboarding: {
      title: 'Set up your company',
      subtitle: "We'll create your books: tax rates, financial years and a chart of accounts.",
      signedInAs: 'Signed in as {{email}} · ',
      logOut: 'Log out',
      companyName: 'Company name',
      nameRequired: 'Enter your company name.',
      country: 'Country',
      countryHint: 'Sets the home currency, tax and financial year.',
      state: 'State',
      gstin: 'GSTIN (optional)',
      gstinHint: 'Leave blank if not registered for GST.',
      gstinState: 'The GSTIN is registered in {{state}}.',
      emirate: 'Emirate',
      trn: 'TRN (optional)',
      trnHint: '15 digits; leave blank if not VAT registered.',
      trnDigits: 'A TRN has 15 digits.',
      creating: 'Setting up your books…',
      create: 'Create company',
    },
    demo: {
      opening: 'Opening the demo…',
      howExplore: 'How would you like to explore?',
      note: 'Sample companies, shared with other visitors. Post anything you like; the books are reset from time to time.',
      tryIt: 'Try the demo',
      accountantTitle: 'As an accountant',
      accountantBody:
        'Work in a sample UAE client: post journals, approve bills, record payments and prepare the VAT return.',
      ownerTitle: 'As a business owner',
      ownerBody:
        'Run Al Noor Trading: raise invoices, record bills and expenses, take payments and see how the business is doing.',
    },
    callback: {
      signingIn: 'Signing you in…',
    },
  },
};

export const ar = {
  publicPages: {
    header: {
      homeAria: 'الصفحة الرئيسية لـ {{product}}',
      siteNav: 'الموقع',
      products: 'المنتجات',
      howItWorks: 'كيف يعمل',
      forAccountants: 'للمحاسبين',
      indiaReady: 'جاهز للهند',
      logIn: 'تسجيل الدخول',
      signUp: 'إنشاء حساب',
    },
    footer: {
      tagline: 'محاسبة للشركات النامية وللمحاسبين الذين يرعونها.',
      products: 'المنتجات',
      more: 'المزيد',
      whoFor: 'لمن صُمّم',
      businessOwners: 'أصحاب الأعمال',
      accountants: 'المحاسبون',
      practices: 'مكاتب المحاسبة',
      credits: '© {{year}} {{product}}. الصور من Unsplash بعدسة {{names}}.',
    },
    hero: {
      badge: 'الآن مع المحاسب الذكي',
      titleLead: 'دفاتر أعمالك كلها،',
      titleAccent: 'في مكان واحد',
      lead: 'يجمع {{product}} الفواتير والمصروفات والحسابات البنكية وضريبة السلع والخدمات والتقارير في مكان واحد، ليعمل صاحب العمل ومحاسبه على الأرقام نفسها كل يوم.',
      getStarted: 'ابدأ الآن',
    },
    trades: {
      title: 'مصمَّم للشركات التي تنمو',
      body: 'للمقاهي والمطاعم والمتاجر والاستوديوهات التي لديها فريق وموردون ومحاسب ينتظر دفاتر مرتبة.',
      cafes: 'المقاهي',
      cafesAlt: 'صاحب مقهى جالس في مقهاه',
      restaurants: 'المطاعم',
      restaurantsAlt: 'صاحب مطعم واقف في صالة الطعام',
      retail: 'متاجر التجزئة',
      retailAlt: 'صاحب متجر عاقد ذراعيه بين رفوفه',
      studios: 'الاستوديوهات والخدمات',
      studiosAlt: 'صاحبة عمل على مكتبها مع حاسوب محمول',
    },
    productList: {
      title: 'كل ما تحتاجه دفاترك، في دفتر أستاذ واحد',
      body: 'ابدأ بالفواتير والحسابات البنكية، ثم فعّل المخزون والمشاريع والفوترة الإلكترونية متى احتجت إليها؛ فكلها تعمل على دفتر الأستاذ نفسه.',
      salesTitle: 'الفواتير والمبيعات',
      salesBody:
        'عروض أسعار وفواتير وإشعارات دائنة ومدفوعات، مع فواتير متكررة وملفات PDF تحمل علامتك التجارية.',
      expensesTitle: 'المصروفات وفواتير الموردين',
      expensesBody: 'سجّل المصروفات وادفع فواتير الموردين وأصدر أوامر الشراء قبل أن تشتري.',
      bankingTitle: 'الحسابات البنكية',
      bankingBody: 'استورد كشوف الحساب وصنّفها بالقواعد وطابق كل حساب حتى آخر روبية.',
      gstTitle: 'ضريبة السلع والخدمات والفوترة الإلكترونية',
      gstBody:
        'ضريبة السلع والخدمات محسوبة على كل سطر، وملخصات شهرية جاهزة للتقديم، وفواتير إلكترونية عند الطلب.',
      reportsTitle: 'التقارير',
      reportsBody:
        'الأرباح والخسائر والميزانية العمومية وميزان المراجعة وأعمار الديون، حسب الفئة أو الموقع أو المشروع.',
      inventoryTitle: 'المخزون',
      inventoryBody: 'المخزون المتوفر بتكلفة FIFO، لتكون تكلفة البضاعة المباعة صحيحة كل شهر.',
      aiTitle: 'المحاسب الذكي',
      aiBody: 'أرفق كشف حساب بنكي أو فاتورة مورد؛ يقرأ كل سطر ويقترح طريقة تسجيله.',
      migrationTitle: 'الانتقال من QuickBooks',
      migrationBody: 'انقل القوائم والأرصدة والسجل، ثم قارن شهرًا بشهر حتى تتطابق الأرقام.',
    },
    flows: {
      title: 'من أول فاتورة حتى إقفال نهاية الشهر',
      sellLabel: 'استلم مستحقاتك',
      sellTitle: 'أرسل فاتورة في دقيقة واعرف متى تُدفع',
      sellBody:
        'أنشئ عروض الأسعار والفواتير مع احتساب ضريبة السلع والخدمات تلقائيًا، وأرسلها بالبريد الإلكتروني من Outss Accounting، وسجّل المدفوعات فور وصولها.',
      sellP1: 'عروض أسعار تتحول إلى فواتير بنقرة واحدة',
      sellP2: 'فواتير متكررة للاشتراكات والإيجارات',
      sellP3: 'تقرير أعمار الديون يبيّن من يدين بماذا ومنذ متى',
      spendLabel: 'تحكّم في الإنفاق',
      spendTitle: 'كل روبية تخرج، معتمدة ومسجّلة',
      spendBody:
        'أصدر أوامر الشراء وحوّلها إلى فواتير موردين وادفع لمورديك في موعدهم. تُسجَّل المصروفات في الحساب الصحيح وبالضريبة الصحيحة.',
      spendP1: 'أوامر شراء تتحول إلى فواتير موردين',
      spendP2: 'أرصدة الموردين والفواتير المستحقة في لمحة',
      spendP3: 'الإيصالات وفواتير الموردين يقرؤها لك المحاسب الذكي',
      bankLabel: 'المطابقة',
      bankTitle: 'حركات بنكية تطابق نفسها',
      bankBody:
        'استورد كشف الحساب، ودع القواعد والمطابقات تنجز العمل الروتيني، وطابق حتى الرصيد الختامي بثقة.',
      bankP1: 'قواعد تصنّف الحركات المتكررة',
      bankP2: 'مطابقة مع الفواتير المفتوحة وفواتير الموردين',
      bankP3: 'شاشة مطابقة تُظهر الفرق حتى يصبح صفرًا',
      closeLabel: 'أقفل الشهر',
      closeTitle: 'دفاتر يعتمدها محاسبك بثقة',
      closeBody:
        'أقفل الفترات بعد مراجعتها، واحتفظ بسجل تدقيق كامل، واستخرج التقارير المهمة، لشركة واحدة أو لعدة شركات.',
      closeP1: 'إقفال الفترات مع سجل تدقيق كامل',
      closeP2: 'تقارير حسب الفئة والموقع والمشروع',
      closeP3: 'مكان واحد يجمع المحاسب وصاحب العمل',
    },
    visuals: {
      invoiceNo: 'فاتورة {{no}}',
      paid: 'مدفوعة',
      catering: 'مستلزمات تموين × 40',
      delivery: 'التوصيل',
      total: 'الإجمالي',
      purchaseOrder: 'أمر شراء',
      approved: 'معتمد',
      bill: 'فاتورة مورد',
      fromPo: 'من {{no}}',
      dueIn14: 'مستحقة خلال 14 يومًا',
      scheduled: 'مجدولة',
      bankHeader: '{{account}} · الحركات البنكية',
      matched: 'تمت مطابقة {{n}} من {{total}}',
      ruleInternet: 'قاعدة: الإنترنت',
      billFromPo: 'فاتورة مورد من {{no}}',
      needsCategory: 'بحاجة إلى تصنيف',
      monthEndClose: 'إقفال نهاية الشهر',
      progress: '{{n}} من {{total}}',
      reconciled: 'تمت مطابقة الحسابات البنكية',
      gstReviewed: 'تمت مراجعة ملخص ضريبة السلع والخدمات',
      signOff: 'اعتماد المحاسب',
      monthLocked: 'تم إقفال الشهر',
      done: 'تم',
      locked: 'مقفل',
    },
    audiences: {
      eyebrow: 'أصحاب الأعمال والمحاسبون',
      title: 'دفتر أستاذ واحد، من زاويتين',
      ownersAlt: 'صاحب مقهى يبتسم في مقهاه',
      ownersTitle: 'لأصحاب الأعمال',
      ownersBody:
        'شاهد النقد والمبيعات والمبالغ المستحقة في شاشة واحدة. أرسل الفواتير واعتمد فواتير الموردين وأجب محاسبك دون سلاسل رسائل لا تنتهي.',
      ownersP1: 'الأرقام المهمة في لوحة معلومات واحدة',
      ownersP2: 'الموافقات وصندوق وارد للمستندات',
      ownersP3: 'يعمل بالطريقة التي يتوقعها مستخدمو QuickBooks',
      accountantsAlt: 'محاسب يراجع دفاتر العملاء على حاسوب محمول',
      accountantsTitle: 'للمحاسبين ومكاتب المحاسبة',
      accountantsBody:
        'أدِر جميع عملائك من حساب واحد. انتقل بين العملاء بنقرة، وأقفل الفترات، وسلّم تقارير يفهمها عملاؤك.',
      accountantsP1: 'قائمة العملاء مع التبديل بنقرة واحدة',
      accountantsP2: 'أدوات المحاسب وسجل التدقيق وإقفال الفترات',
      accountantsP3: 'الانتقال من QuickBooks مع تشغيل متوازٍ',
    },
    india: {
      photoAlt: 'مقهى مشرق بطاولات خشبية بجوار نوافذ عالية',
      title: 'مصمَّم للطريقة التي تمسك بها الشركات الهندية دفاترها',
      body: 'ضريبة السلع والخدمات والروبية والفوترة الإلكترونية جاهزة منذ البداية. تعمل مع عميل في الخارج؟ الدول والعملات الأخرى على بُعد إعداد واحد.',
      gst: 'احتساب CGST وSGST وIGST حسب مكان التوريد',
      rupee: 'المبالغ باللاكه والكرور، كما يقرؤها فريقك',
      hsn: 'رموز HSN وSAC على المنتجات وسطور الفواتير',
      eInvoiceKey: 'الفاتورة الإلكترونية',
      eInvoice: 'الفوترة الإلكترونية عندما يتطلبها حجم مبيعاتك',
    },
    cta: {
      title: 'انقل دفاترك إلى {{product}}',
      body: 'أنشئ حسابك، وادعُ محاسبك، وابدأ بأرقام اليوم.',
    },
    preview: {
      windowTitle: '{{product}} · لوحة المعلومات',
      newButton: '+ جديد',
      dashboard: 'لوحة المعلومات',
      banking: 'البنوك',
      sales: 'المبيعات',
      expenses: 'المصروفات',
      reports: 'التقارير',
      accounting: 'المحاسبة',
      overview: 'نظرة عامة على الأعمال · {{company}}',
      thisQuarter: 'هذا الربع',
      income: 'الإيرادات',
      netProfit: 'صافي الربح',
      salesTrend: 'اتجاه المبيعات',
      invoices: 'الفواتير',
      overdue: 'متأخرة السداد',
      notDueYet: 'غير مستحقة بعد',
      paid30: 'مدفوعة (30 يومًا)',
    },
    world: {
      eyebrow: 'حول العالم',
      titleLead: 'أصدر الفاتورة بعملة عميلك.',
      titleRest: 'واستخرج تقاريرك بعملتك.',
      body: 'فواتير المبيعات وفواتير الموردين بـ {{n}} عملة، مع أسعار صرف حسب التاريخ وإعادة تقييم والعدد الصحيح من الخانات العشرية لكل عملة.',
      currencies: 'عملة',
      reelTitle: 'إجمالي فاتورة واحدة',
      play: 'تشغيل حركة العملات',
      pause: 'إيقاف حركة العملات مؤقتًا',
      example: 'على سبيل المثال، {{base}} يساوي {{others}}.',
      decimals: 'الدينار الكويتي (KWD) بثلاث خانات عشرية، والين الياباني (JPY) بلا خانات.',
    },
    authCard: {
      homeAria: 'الرئيسية',
      backHome: 'العودة إلى الرئيسية',
    },
    login: {
      title: 'سجّل الدخول للمتابعة',
      newTo: 'جديد على Outss Accounting؟',
      createAccount: 'أنشئ حسابًا',
      alreadySignedIn: 'أنت مسجّل الدخول بالفعل.',
      open: 'افتح Outss Accounting',
      failed: 'تعذّر فتح صفحة تسجيل الدخول. تحقق من اتصالك وحاول مرة أخرى.',
      email: 'البريد الإلكتروني',
      emailPlaceholder: 'أدخل بريدك الإلكتروني',
      opening: 'جارٍ فتح صفحة تسجيل الدخول…',
      continue: 'متابعة',
      passwordNext: 'ستُدخل كلمة المرور في صفحة تسجيل الدخول الآمنة في الخطوة التالية.',
    },
    signup: {
      title: 'أنشئ حسابك للمتابعة',
      subtitle: 'أنشئ حسابك في Outss Accounting ببريد العمل، ثم أعدّ شركتك.',
      haveAccount: 'لديك حساب بالفعل؟',
      failed: 'تعذّر فتح صفحة إنشاء الحساب. تحقق من اتصالك وحاول مرة أخرى.',
      workEmail: 'بريد العمل الإلكتروني',
      perk1: 'الفواتير والمصروفات والحسابات البنكية وضريبة السلع والخدمات في مكان واحد',
      perk2: 'شارك الدفاتر نفسها مع محاسبك',
      perk3: 'تقارير حسب الفئة والموقع والمشروع',
      terms: 'بإنشاء حساب، فإنك توافق على شروط الاستخدام وسياسة الخصوصية لدى Outss.',
      opening: 'جارٍ فتح صفحة إنشاء الحساب…',
    },
    onboarding: {
      title: 'أعدّ شركتك',
      subtitle: 'سننشئ دفاترك: نسب الضريبة والسنوات المالية ودليل الحسابات.',
      signedInAs: 'مسجّل الدخول باسم {{email}} · ',
      logOut: 'تسجيل الخروج',
      companyName: 'اسم الشركة',
      nameRequired: 'أدخل اسم شركتك.',
      country: 'الدولة',
      countryHint: 'تحدد العملة الأساسية والضريبة والسنة المالية.',
      state: 'الولاية',
      gstin: 'GSTIN (اختياري)',
      gstinHint: 'اتركه فارغًا إن لم تكن مسجلًا في ضريبة السلع والخدمات.',
      gstinState: 'رقم GSTIN مسجّل في {{state}}.',
      emirate: 'الإمارة',
      trn: 'TRN (اختياري)',
      trnHint: '15 رقمًا؛ اتركه فارغًا إن لم تكن مسجلًا في ضريبة القيمة المضافة.',
      trnDigits: 'يتكون رقم TRN من 15 رقمًا.',
      creating: 'جارٍ إعداد دفاترك…',
      create: 'إنشاء الشركة',
    },
    demo: {
      opening: 'جارٍ فتح العرض التجريبي…',
      howExplore: 'كيف تودّ أن تستكشف؟',
      note: 'شركات نموذجية مشتركة مع زوار آخرين. سجّل ما تشاء؛ تُعاد الدفاتر إلى حالتها الأصلية من وقت لآخر.',
      tryIt: 'جرّب العرض التجريبي',
      accountantTitle: 'كمحاسب',
      accountantBody:
        'اعمل على عميل نموذجي في الإمارات: سجّل قيود اليومية، واعتمد فواتير الموردين، وسجّل المدفوعات، وأعدّ إقرار ضريبة القيمة المضافة.',
      ownerTitle: 'كصاحب عمل',
      ownerBody:
        'أدِر شركة النور للتجارة: أصدر الفواتير، وسجّل فواتير الموردين والمصروفات، واستلم المدفوعات، وتابع أداء أعمالك.',
    },
    callback: {
      signingIn: 'جارٍ تسجيل دخولك…',
    },
  },
};
